import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';
import { Progress } from './progress';
import { Spinner } from './spinner';

const progressIndicatorVariants = cva(
  'flex items-center gap-3 p-4 rounded-lg border bg-card text-card-foreground',
  {
    variants: {
      variant: {
        default: 'border-border',
        success: 'border-green-200 bg-green-50 text-green-900',
        warning: 'border-yellow-200 bg-yellow-50 text-yellow-900',
        error: 'border-red-200 bg-red-50 text-red-900',
      },
      size: {
        sm: 'p-3 text-sm',
        default: 'p-4',
        lg: 'p-6 text-lg',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ProgressIndicatorProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof progressIndicatorVariants> {
  progress?: number;
  total?: number;
  current?: number;
  label?: string;
  description?: string;
  showSpinner?: boolean;
  showPercentage?: boolean;
  estimatedTime?: string;
  itemsProcessed?: { current: number; total: number };
}

const ProgressIndicator = React.forwardRef<HTMLDivElement, ProgressIndicatorProps>(
  ({
    className,
    variant,
    size,
    progress,
    total,
    current,
    label,
    description,
    showSpinner = true,
    showPercentage = true,
    estimatedTime,
    itemsProcessed,
    ...props
  }, ref) => {
    // Calculate progress percentage
    const progressValue = React.useMemo(() => {
      if (progress !== undefined) return progress;
      if (current !== undefined && total !== undefined && total > 0) {
        return (current / total) * 100;
      }
      return undefined;
    }, [progress, current, total]);

    // Determine progress variant based on completion
    const progressVariant = React.useMemo(() => {
      if (variant === 'error') return 'error';
      if (variant === 'warning') return 'warning';
      if (progressValue === 100) return 'success';
      return 'default';
    }, [variant, progressValue]);

    return (
      <div
        ref={ref}
        className={cn(progressIndicatorVariants({ variant, size, className }))}
        {...props}
      >
        {showSpinner && progressValue !== 100 && (
          <Spinner size={size === 'sm' ? 'sm' : 'default'} />
        )}
        
        <div className="flex-1 space-y-2">
          <div className="flex items-center justify-between">
            {label && (
              <div className="font-medium">{label}</div>
            )}
            {showPercentage && progressValue !== undefined && (
              <div className="text-sm text-muted-foreground">
                {Math.round(progressValue)}%
              </div>
            )}
          </div>
          
          {progressValue !== undefined && (
            <Progress
              value={progressValue}
              variant={progressVariant}
              size={size === 'sm' ? 'sm' : size === 'lg' ? 'lg' : 'default'}
            />
          )}
          
          <div className="space-y-1 text-sm text-muted-foreground">
            {description && <div>{description}</div>}
            
            {itemsProcessed && (
              <div>
                {itemsProcessed.current} of {itemsProcessed.total} items processed
              </div>
            )}
            
            {estimatedTime && (
              <div>Estimated time remaining: {estimatedTime}</div>
            )}
          </div>
        </div>
      </div>
    );
  }
);
ProgressIndicator.displayName = 'ProgressIndicator';

export { ProgressIndicator, progressIndicatorVariants };